// Apply a resolution moment chosen in the preprocess panel (design:
// experimental/sooth-agents/docs/2026-09-21-ask-to-clarify-design.md).
//
// Two entry states, one outcome — a forecast running on the chosen moment:
//   • HELD (`pp.awaitingChoice`): submit found a genuine ambiguity and did not start
//     the forecast. Stamp the moment onto the payload and start it now, on the same
//     pending run.
//   • RUNNING: the user used ✎ Edit after the forecast started. Cancel it and rerun on
//     a fresh pending run — the same shape as editResubmit, minus the re-preprocess,
//     because the question text has not changed. The panel stays open, paused.
//
// The moment is written with basis `explicit` (withResolutionMoment), so neither the
// proxy's contract seed nor the forecast builder re-guesses it.
import { recordContractEdit } from "../lib/contractTelemetry";
import { recordTextEvent } from "../lib/telemetry/groups.js";
import { newMessageId } from "../lib/chatTurn.js";
import { runRemoved, runCreated, activeSet, contractSet } from "../lib/forecast/reducer.js";
import { makeRun, STATUS } from "../lib/forecast/model.js";
import { effectiveTriggerModel } from "../lib/forecast/engines.js";
import { contractFromPreprocessed, deadlineOf } from "../lib/forecast/forecastClient.js";
import { resolutionOptionsOf, withResolutionMoment } from "../lib/forecast/resolutionChoice.js";
import { editBufferOf } from "../lib/forecast/multiway.js";
import { bindIntent } from "../lib/telemetry/intentDeps.js";
import { intentEmitter } from "../lib/telemetry/intentEmitter.js";
import { beginIntent } from "../lib/telemetry/instrument.js";
import type { CommandDeps } from "./deps.js";

type AnyFn = (...args: any[]) => any;
type Ref<T = any> = { current: T };

/** Full deps: the shared base plus the React handles this command drives. */
export interface ApplyResolutionMomentDeps extends CommandDeps {
  ui: {
    setForecastingPhase: AnyFn;
    setPendingPreprocess: AnyFn;
    compareMode: boolean;
    triggerLlm: any;
  };
  refs: { forecastAbortRef: Ref; slideCache: Ref };
  callbacks: { autoZoom: AnyFn; dismiss: AnyFn; doForecast: AnyFn };
}

/** Args: the panel snapshot, and the moment picked (`{moment_utc, zone, label}`). */
export interface ApplyResolutionMomentArgs {
  pp: any;
  choice: { moment_utc: string; zone?: string; label?: string };
}

const uid = () =>
  typeof crypto !== "undefined" && crypto.randomUUID
    ? crypto.randomUUID()
    : `${Date.now()}-${Math.floor(Math.random() * 1e9)}`;

/** Stamp `choice` onto the preprocessed question and (re)start the forecast on it. */
export function applyResolutionMoment(
  rawDeps: ApplyResolutionMomentDeps,
  { pp, choice }: ApplyResolutionMomentArgs,
): void {
  if (!pp || pp.submitting) return;
  // The design's success measures: how often we asked, and how often the answer was
  // NOT our first guess (option_index > 0; -1 is a custom time typed in ✎ Edit).
  const optionIndex = resolutionOptionsOf(pp.question).findIndex(
    (o: any) => o.moment_utc === choice.moment_utc,
  );
  // The user's pick (P2 design §4.6); the forecast it starts keeps following the submit's
  // own intent.
  const intent = beginIntent(
    "resolution.apply",
    { forecast_id: pp.clientId ?? null },
    { origin: optionIndex === -1 ? "resolution.custom-apply" : "resolution.option" },
  );
  const deps = bindIntent(rawDeps, intent);
  const updated = withResolutionMoment(pp.question, choice);
  const when = deadlineOf(updated);
  if (!when || when <= new Date()) {
    deps.ui.setPendingPreprocess((p: any) =>
      p ? { ...p, submitError: "That moment is already in the past — pick a later one." } : null,
    );
    intent.end("error");
    return;
  }
  recordContractEdit({
    field: "resolution_moment",
    forecastId: pp.clientId ?? null,
    changed:
      updated.custom_metadata?.resolution_moment_utc !==
      pp.question.custom_metadata?.resolution_moment_utc,
    asked: !!pp.awaitingChoice,
    option_index: optionIndex,
  });
  // Only a time the user typed is their text; an offered option is ours.
  if (optionIndex === -1) {
    recordTextEvent({
      event: "text.resolution_moment",
      value: choice.zone ? `${choice.moment_utc} ${choice.zone}` : choice.moment_utc,
      forecast_id: deps.store.getState()?.runsById?.[pp.clientId]?.forecastId ?? null,
    });
  }
  const q = updated.question_text || pp.original;

  let runId = pp.clientId;
  // A held forecast persists its question for the first time on this run, under the
  // submit's msg_id; a rerun of a running one may follow an already persisted question,
  // so it is a message of its own.
  const msgId = pp.awaitingChoice ? (pp.msgId ?? null) : newMessageId();
  if (!pp.awaitingChoice) {
    // RUNNING: cancel before anything else so the old stream's late response cannot
    // clobber the rerun (doForecast bails when forecastAbortRef is no longer its own).
    if (deps.refs.forecastAbortRef.current) {
      deps.refs.forecastAbortRef.current.abort();
      deps.refs.forecastAbortRef.current = null;
    }
    deps.store.dispatch(runRemoved(pp.clientId));
    runId = "fc-pending-" + uid();
    intentEmitter().retargetRun(pp.clientId, runId);
    deps.store.dispatch(
      runCreated(
        makeRun({
          runId,
          conversationId: runId,
          question: q,
          status: STATUS.PREPROCESSING,
          triggerModel: deps.ui.compareMode ? effectiveTriggerModel(deps.ui.triggerLlm) : null,
        }),
      ),
    );
    deps.store.dispatch(activeSet(runId));
    deps.ui.setForecastingPhase("awaiting");
    deps.refs.slideCache.current = {};
  }

  const fallback = when.toISOString().slice(0, 10);
  const contract = contractFromPreprocessed(updated, pp.original, fallback);
  deps.store.dispatch(contractSet(runId, contract as any));
  deps.callbacks.autoZoom(contract.resolution_date);
  if (pp.awaitingChoice) {
    // The pick IS the confirmation for a held forecast.
    deps.callbacks.dismiss();
  } else {
    // An edit from ✎ Edit stays IN the panel: show the new moment, keep the countdown
    // paused, and re-point the panel at the fresh run so its render gate still matches.
    // The edit buffer follows the reworded question, or a later Edit would bring the old date back.
    deps.ui.setPendingPreprocess((p: any) =>
      p
        ? {
            ...p,
            question: updated,
            editText: updated.question_text
              ? editBufferOf(updated.question_text, p.routing)
              : p.editText,
            clientId: runId,
            msgId,
            submitError: null,
            paused: true,
            // The pick closed the date editor; a refused pick (above) keeps it counted.
            editingDate: null,
          }
        : null,
    );
  }
  deps.callbacks.doForecast(q, pp.engine, updated, null, runId, msgId, pp.routing);
  intent.end("ok");
}
